import { OrganizationResponseDto, UpdateOrganizationAiRequestDto } from "@/lib/interfaces/organizations";
import { Box, Button, Flex, Switch, Text, Textarea } from "@chakra-ui/react";
import { Save } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useApiRequest from "../../lib/hooks/useRequest";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";

const KandidatKI = ({
  organization,
  setOrganization,
}: {
  organization: OrganizationResponseDto;
  setOrganization: Dispatch<SetStateAction<OrganizationResponseDto | undefined>>;
}) => {
  const { apiRequest } = useApiRequest();

  const formSchema = z.object({
    aboutUs: z.string().optional(),
    addressingMode: z.enum(["DU", "SIE"]),
    promptInstructions: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      aboutUs: organization.aboutUs ?? "",
      addressingMode: organization.addressingMode ?? "DU",
      promptInstructions: organization.promptInstructions ?? "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const req: UpdateOrganizationAiRequestDto = {
      aboutUs: values.aboutUs,
      addressingMode: values.addressingMode,
      promptInstructions: values.promptInstructions,
    };
    const res = await apiRequest<OrganizationResponseDto>(`organizations/ai`, "PATCH", {
      body: req,
      toast: { toastText: "Daten erfolgreich aktualisiert" },
    });

    if (res.data) {
      setOrganization({
        ...organization,
        ...res.data,
      });
    }
  }

  if (!organization.aiEnabled) {
    return (
      <Flex className="h-full flex-col items-center justify-center">
        <Text className="text-lg font-bold text-gray-600 mb-4">Kandidat KI nicht verfügbar</Text>
        <Text className="text-gray-400 text-sm text-center">
          Die Kandidat KI Funktion ist für Ihre Organisation nicht aktiviert. Bitte wenden Sie sich an Ihren
          Administrator, um diese Funktion zu aktivieren.
        </Text>
      </Flex>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col gap-8 h-full bg-white">
        <Flex className="h-full flex-col">
          <Text className="text-lg font-bold">Kandidat KI Einstellungen</Text>
          <Text className="text-gray-400 text-sm">
            Konfigurieren Sie hier die Einstellungen für die Kandidat KI, einschließlich der Ansprache und Informationen
            über Ihr Unternehmen.
          </Text>

          <Box className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="addressingMode"
              render={({ field }) => (
                <FormItem className="flex gap-4 w-full grow">
                  <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">
                    Ansprache <span className="text-primary">*</span>
                  </FormLabel>
                  <Box className="w-full flex items-center gap-4">
                    <Text
                      className={`text-sm ${field.value === "SIE" ? "text-green-600 font-semibold" : "text-gray-500"}`}
                    >
                      Sie
                    </Text>
                    <Switch
                      isChecked={field.value === "DU"}
                      onChange={(e) => field.onChange(e.target.checked ? "DU" : "SIE")}
                      colorScheme="green"
                      sx={{
                        "& .chakra-switch__track": {
                          bg: field.value === "DU" ? "green.500" : "green.200",
                        },
                        "& .chakra-switch__thumb": {
                          bg: "white",
                        },
                      }}
                    />
                    <Text
                      className={`text-sm ${field.value === "DU" ? "text-green-600 font-semibold" : "text-gray-500"}`}
                    >
                      Du
                    </Text>
                  </Box>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="promptInstructions"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3 w-full grow">
                  <FormLabel className="w-full font-bold mt-4 text-md">Prompt Anweisungen</FormLabel>
                  <Box className="w-full">
                    <Textarea
                      rows={6}
                      resize="vertical"
                      placeholder="Beschreiben Sie die Anweisungen für die KI, um personalisierte Antworten zu generieren..."
                      {...field}
                    />
                  </Box>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="aboutUs"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3 w-full grow">
                  <FormLabel className="w-full font-bold mt-4 text-md">Über uns</FormLabel>
                  <Box className="w-full">
                    <FormControl>
                      <Textarea
                        placeholder="Beschreiben Sie Ihr Unternehmen, Ihre Werte und was Sie ausmacht..."
                        rows={6}
                        resize="vertical"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </Box>
                  <Text className="text-sm text-gray-500">
                    Diese Informationen können von der KI verwendet werden, um personalisierte Antworten zu generieren.
                  </Text>
                </FormItem>
              )}
            />
          </Box>
        </Flex>

        <Flex className="gap-4 justify-end mt-auto">
          <Button
            colorScheme="primary"
            color="white"
            className="flex gap-2 p-2 text-white ml-auto mt-4"
            variant="solid"
            type="submit"
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <Save />
            </Box>
            <span>Speichern</span>
          </Button>
        </Flex>
      </form>
    </Form>
  );
};

export default KandidatKI;
